import { Directive, inject, Input, Output, EventEmitter, computed, ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, OnDestroy, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { BaseCrudComponent } from '../base/base-crud.component';
import { ChildConfig } from './models/hierarchy-config';
import { FieldDefinition } from '../fields/field-definition';
import { ZnDropdownMenuComponent, DropdownMenuItem } from '../zn-dropdown-menu/zn-dropdown-menu.component';
import { FieldMapperHelper } from '../fields/field-mapper.helper';

@Directive()
export abstract class BaseHierarchicalCrudComponent<
  TDto extends { id?: number; concurrencyStamp?: string },
  TCreateDto,
  TUpdateDto,
  TExcelDto
> extends BaseCrudComponent<TDto, TCreateDto, TUpdateDto, TExcelDto> implements OnDestroy, AfterViewInit, OnChanges {

  private appRef = inject(ApplicationRef);
  private envInjector = inject(EnvironmentInjector);

  private dropdownMenuRef?: ComponentRef<ZnDropdownMenuComponent>;
  private dropdownSubscriptions: Subscription[] = [];
  private activeChevronCell?: HTMLElement;

  @Input() parentId?: number;
  @Input() parentFilterField?: string;
  @Input() isUsedInHierarchy: boolean = false;
  @Output() drillDownRequested = new EventEmitter<{ row: TDto; childEntityName: string }>();

  isChildView = computed(() => this.parentId !== undefined);
  hasChildren = computed(() => {
    const children = this.config.children;
    const getChildren = this.config.getChildren;

    return (children && children.length > 0) || !!getChildren;
  });
  fieldsWithChevron = computed(() => this.getFieldsWithChevron());

  constructor() {
    super();
  }

  override ngOnDestroy(): void {
    this.closeDropdown();
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    this.updateBackendForcedFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parentId'] || changes['parentFilterField']) {
      this.updateBackendForcedFilters();
    }
  }

  protected override buildListQueryParams(queryString: string): Record<string, any> {
    const params = super.buildListQueryParams(queryString);

    // Constrain even the initial request, before the grid's forced filters are ready.
    if (this.parentId !== undefined && this.parentFilterField) {
      delete params[`${this.parentFilterField}From`];
      delete params[`${this.parentFilterField}To`];
      params[this.parentFilterField] = [this.parentId];
    }

    return params;
  }

  override create(): void {
    if (this.parentId !== undefined && this.parentFilterField) {
      this.selectedEntity = {
        [this.parentFilterField]: this.parentId
      } as unknown as TDto;
    } else {
      this.selectedEntity = {} as TDto;
    }

    const fields = FieldMapperHelper.mapFieldsForForm(this.fields, this.selectedEntity, this.injector);

    this.genericForm.open({
      title: `${this.localizationResource.LanguageService}::TableNewRegistry`,
      fields,
      entityLocaleKey: this.config.localizationKey,
      mode: 'create'
    });
  }

  override edit(row: TDto): void {
    this.selectedEntity = { ...row };
    const fields = FieldMapperHelper.mapFieldsForForm(this.fields, row, this.injector).map(f => ({
      ...f,
      required: f.required,
      validators: f.validators
    }));

    this.genericForm.open({
      title: `${this.localizationResource.LanguageService}::EditRecord`,
      fields,
      entityLocaleKey: this.config.localizationKey,
      mode: 'edit'
    });
  }

  private updateBackendForcedFilters(): void {
    if (!this.crudPage || !this.parentFilterField || this.parentId === undefined) {
      return;
    }

    const backendService = this.crudPage?.gridTable?.gridOptions?.backendServiceApi?.service as any;
    if (backendService?.setForcedFilters) {
      backendService.setForcedFilters({ [this.parentFilterField]: [this.parentId] });
    }
  }

  protected getFieldsWithChevron(): FieldDefinition[] {
    let fieldsToUse = this.fields;

    // Filter hidden fields when in child context
    if (this.isChildView()) {
      fieldsToUse = this.fields.filter(f => !f.fieldOptions.hidden);
    }

    if (!this.hasChildren()) {
      return fieldsToUse;
    }

    const chevronField = this.createChevronField();
    return [chevronField, ...fieldsToUse];
  }

  getSimplifiedFields(): FieldDefinition[] {
    return this.fields.filter(f =>
      f.key !== '_checkbox' &&
      f.key !== '_edit' &&
      f.key !== '_read' &&
      f.key !== '_chevron'
    );
  }

  private createChevronField(): FieldDefinition {
    const formatter = (row: number, cell: number, value: any, columnDef: any, dataContext: any) => {
      const children = this.config.getChildren
        ? this.config.getChildren(dataContext, this.config.children || [])
        : this.config.children || [];

      if (dataContext?.error || dataContext?.isSkeleton || children.length === 0) {
        return '';
      }
      return '<i class="bi bi-chevron-right chevron-icon"></i>';
    };

    const onCellClick = (e: Event, args: any) => {
      e.stopPropagation();

      const cellToActivate = args.grid.getColumns().length > args.cell + 1
        ? args.cell + 1
        : args.cell;
      args.grid.setActiveCell(args.row, cellToActivate);

      const cellNode = args.grid.getCellNode(args.row, args.cell);

      this.showChildMenu(e, args.dataContext, cellNode);
    };

    return {
      key: '_chevron',
      label: '',
      column: {
        id: 'chevron',
        field: 'id',
        excludeFromColumnPicker: true,
        excludeFromGridMenu: true,
        excludeFromHeaderMenu: true,
        focusable: false,
        formatter,
        minWidth: 32,
        maxWidth: 32,
        cssClass: 'chevron-column',
        onCellClick,
      },
      fieldOptions: {} as any,
    };
  }

  private showChildMenu(event: Event, row: TDto, cellElement?: HTMLElement): void {
    event.stopPropagation();

    let children: ChildConfig[] = [];

    if (this.config.getChildren) {
      children = this.config.getChildren(row, this.config.children || []);
    } else if (this.config.children) {
      children = this.config.children;
    }

    if (children.length === 0 || !row.id) {
      return;
    }

    this.closeDropdown();

    if (cellElement) {
      cellElement.classList.add('chevron-open');
      this.activeChevronCell = cellElement;
    }

    const menuItems: DropdownMenuItem[] = children.map(child => ({
      id: child.entityName,
      label: child.localizationKey
    }));

    if (!cellElement) return;

    const rect = cellElement.getBoundingClientRect();
    const dropdownHeight = children.length * 40 + 16;
    const viewportHeight = window.innerHeight;

    let top = rect.bottom;

    if (rect.bottom + dropdownHeight > viewportHeight) {
      top = rect.top - dropdownHeight;
    }

    const position = {
      x: rect.left,
      y: top + window.scrollY
    };

    this.dropdownMenuRef = createComponent(ZnDropdownMenuComponent, {
      environmentInjector: this.envInjector
    });

    this.dropdownMenuRef.instance.items = menuItems;
    this.dropdownMenuRef.instance.position = position;
    this.dropdownMenuRef.instance.isVisible = true;

    const itemSelectedSub = this.dropdownMenuRef.instance.itemSelected.subscribe((entityName: string) => {
      const selectedChild = children.find(c => c.entityName === entityName);
      if (selectedChild) {
        this.onChevronClick(row, selectedChild);
      }
      this.closeDropdown();
    });

    const closedSub = this.dropdownMenuRef.instance.closed.subscribe(() => {
      this.closeDropdown();
    });

    this.dropdownSubscriptions.push(itemSelectedSub, closedSub);

    this.appRef.attachView(this.dropdownMenuRef.hostView);
    const domElem = (this.dropdownMenuRef.hostView as any).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);
  }

  private closeDropdown(): void {
    if (this.dropdownMenuRef) {
      this.dropdownSubscriptions.forEach(sub => sub.unsubscribe());
      this.dropdownSubscriptions = [];

      this.appRef.detachView(this.dropdownMenuRef.hostView);
      this.dropdownMenuRef.destroy();
      this.dropdownMenuRef = undefined;
    }

    if (this.activeChevronCell) {
      this.activeChevronCell.classList.remove('chevron-open');
      this.activeChevronCell = undefined;
    }
  }

  protected onChevronClick(row: TDto, childConfig: ChildConfig): void {
    if (!row.id) {
      return;
    }

    this.drillDownRequested.emit({
      row,
      childEntityName: childConfig.entityName
    });
  }

  protected getDisplayName(row: TDto): string {
    return (row as any).name || `${this.config.entityName} ${row.id}`;
  }
}
